import { useState, useEffect, useMemo } from 'react';
import { Book } from '@/types/book';
import { SearchCache } from '@/lib/searchCache';
import { useBooks } from './use-books';

export function useCachedSearch(query: string) {
  const { allBooks, isLoading: booksLoading } = useBooks();
  const [isSearching, setIsSearching] = useState(false);

  // Clear expired cache entries on mount and periodically
  useEffect(() => {
    SearchCache.clearExpired();
    
    // Set up periodic cleanup every hour
    const cleanupInterval = setInterval(() => {
      SearchCache.clearExpired();
    }, 60 * 60 * 1000); // 1 hour
    
    return () => clearInterval(cleanupInterval);
  }, []);

  const searchResults = useMemo(() => {
    if (!query || query.trim() === '') {
      return allBooks;
    }

    const trimmedQuery = query.trim().toLowerCase();
    
    // Check cache first
    const cachedResults = SearchCache.get(trimmedQuery);
    if (cachedResults) {
      return cachedResults;
    }

    // If not in cache and books are still loading, return empty array
    if (booksLoading) {
      return [];
    }

    // Perform search
    setIsSearching(true);
    
    const searchTerms = trimmedQuery.split(" ");
    const results = allBooks.filter((book: Book) => {
      return searchTerms.some(term => 
        book.title?.toLowerCase().includes(term) || 
        (book.reference_movie && book.reference_movie.toLowerCase().includes(term)) ||
        book.author?.toLowerCase().includes(term)
      );
    });

    // Cache the results
    SearchCache.set(trimmedQuery, results);
    
    setIsSearching(false);
    return results;
  }, [query, allBooks, booksLoading]);

  return {
    searchResults,
    isLoading: booksLoading || isSearching,
    clearCache: SearchCache.clear
  };
}